/**
 * Data Renderer - For Finance / CRM / Structured Output
 * Used when: output.structured_json exists
 * 
 * Features:
 * - Tables or key-value cards
 * - No prose-first layouts
 * - Numeric emphasis
 * - Inline validation where possible
 * - Contextual action buttons
 */

import React from 'react';
import {
  ArrowsClockwise,
  CheckCircle,
  CurrencyDollar,
  Database,
  DownloadSimple,
  FileText,
  Minus,
  Plus,
  TrendDown,
  TrendUp,
  WarningCircle
} from '@phosphor-icons/react';

// Format numeric values
const formatValue = (value, type) => {
  if (value === null || value === undefined) return '—';
  
  if (type === 'currency' || (typeof value === 'number' && Math.abs(value) >= 100)) {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  }
  
  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  return String(value);
};

// Get trend indicator
const getTrendIndicator = (value, type) => {
  if (typeof value !== 'number') return null;
  
  if (type === 'change' || type === 'delta' || type === 'growth') {
    if (value > 0) return { icon: TrendUp, color: 'text-green-400' };
    if (value < 0) return { icon: TrendDown, color: 'text-red-400' };
    return { icon: Minus, color: 'text-gray-400' };
  }
  
  return null;
};

// Detect if value looks like currency
const isCurrencyField = (key) => {
  const lower = key.toLowerCase();
  return lower.includes('amount') || lower.includes('price') || lower.includes('cost') || 
         lower.includes('revenue') || lower.includes('total') || lower.includes('balance') ||
         lower.includes('salary') || lower.includes('budget');
};

// Format key for display
const formatKey = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Render a data table from array of objects
const DataTable = ({ data, title }) => {
  if (!Array.isArray(data) || data.length === 0) return null;
  
  // Get columns from first item
  const columns = Object.keys(data[0] || {});
  
  return (
    <div className="bg-[#111111] border border-[rgba(255,255,255,0.08)] rounded-xl overflow-hidden">
      {title && (
        <div className="px-4 py-3 border-b border-[rgba(255,255,255,0.08)]">
          <h3 className="text-sm font-medium text-white flex items-center gap-2">
            <Database size={14} className="text-gray-400" />
            {title}
          </h3>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[rgba(255,255,255,0.02)]">
              {columns.map((col, idx) => (
                <th 
                  key={col} 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-[rgba(255,255,255,0.05)]"
                >
                  {formatKey(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIdx) => (
              <tr 
                key={rowIdx}
                className="hover:bg-[rgba(255,255,255,0.02)] transition-colors"
              >
                {columns.map((col, colIdx) => {
                  const value = row[col];
                  const isCurrency = isCurrencyField(col);
                  const trend = getTrendIndicator(value, col);
                  const TrendIcon = trend?.icon;
                  
                  return (
                    <td 
                      key={col}
                      className={`px-4 py-3 text-sm border-b border-[rgba(255,255,255,0.05)] ${
                        typeof value === 'number' ? 'text-right font-mono' : ''
                      } ${colIdx === 0 ? 'text-white font-medium' : 'text-gray-300'}`}
                    >
                      <span className="flex items-center gap-2 justify-end">
                        {formatValue(value, isCurrency ? 'currency' : null)}
                        {TrendIcon && <TrendIcon size={14} className={trend.color} />}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Render key-value cards
const KeyValueCards = ({ data, title }) => {
  if (!data || typeof data !== 'object') return null;
  
  const entries = Object.entries(data).filter(([k, v]) => 
    typeof v !== 'object' || v === null
  );
  
  if (entries.length === 0) return null;
  
  return (
    <div className="bg-[#111111] border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
      {title && (
        <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
          <Database size={14} className="text-gray-400" />
          {title}
        </h3>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {entries.map(([key, value]) => {
          const isCurrency = isCurrencyField(key);
          
          return (
            <div key={key} className="p-3 bg-[rgba(255,255,255,0.02)] rounded-lg">
              <p className="text-xs text-gray-500 mb-1">{formatKey(key)}</p>
              <p className={`font-medium ${typeof value === 'number' ? 'text-white font-mono text-lg' : 'text-gray-200'}`}>
                {formatValue(value, isCurrency ? 'currency' : null)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const DataRenderer = ({ content, outputData, onAction }) => {
  const structuredJson = content?.structured_json || content?.json_data || content?.data || {};
  
  // Separate arrays (tables) from objects (key-value)
  const arrays = {};
  const objects = {};
  const primitives = {};
  
  Object.entries(structuredJson).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      arrays[key] = value;
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      objects[key] = value;
    } else {
      primitives[key] = value;
    }
  });
  
  // Check if we have summary/totals
  const hasSummary = Object.keys(primitives).length > 0;
  const hasTables = Object.keys(arrays).length > 0;
  const hasObjects = Object.keys(objects).length > 0;
  
  return (
    <div className="space-y-6">
      {/* Summary metrics */}
      {hasSummary && (
        <KeyValueCards data={primitives} title="Summary" />
      )}
      
      {/* Data tables */}
      {Object.entries(arrays).map(([key, data]) => (
        <DataTable key={key} data={data} title={formatKey(key)} />
      ))}
      
      {/* Nested objects as key-value */}
      {Object.entries(objects).map(([key, data]) => (
        <KeyValueCards key={key} data={data} title={formatKey(key)} />
      ))}
      
      {/* Empty state */}
      {!hasSummary && !hasTables && !hasObjects && (
        <div className="p-6 text-center">
          <Database className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No structured data available</p>
        </div>
      )}
      
      {/* Action bar */}
      <div className="flex flex-wrap gap-2 pt-4 border-t border-[rgba(255,255,255,0.08)]">
        <button 
          onClick={() => onAction?.('export', 'csv')}
          className="px-4 py-2 text-sm bg-[#6366F1] text-white rounded-lg hover:bg-[#5558E3] transition-colors flex items-center gap-2"
        >
          <DownloadSimple size={14} />
          Export Data
        </button>
        <button 
          onClick={() => onAction?.('create_record')}
          className="px-4 py-2 text-sm bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-2"
        >
          <Plus size={14} />
          Create Record
        </button>
        <button 
          onClick={() => onAction?.('refresh')}
          className="px-4 py-2 text-sm bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-2"
        >
          <ArrowsClockwise size={14} />
          ArrowsClockwise
        </button>
      </div>
      
      {/* Data report message */}
      <p className="text-xs text-gray-500 text-center">
        This data report is ready for review.
      </p>
    </div>
  );
};

export default DataRenderer;
